import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
} from "react-icons/fa";
import {
  fetchGalleryImages,
  deleteGalleryImage,
  toggleImageStatus,
} from "../../store/slices/gallerySlice";

const AdminGallery = () => {
  const dispatch = useDispatch();
  const { images, loading, error, pagination } = useSelector((state) => state.gallery);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  useEffect(() => {
    dispatch(
      fetchGalleryImages({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        category: selectedCategory,
      }),
    );
  }, [dispatch, currentPage, searchTerm, selectedCategory]);

  const handleDelete = async (imageId) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      await dispatch(deleteGalleryImage(imageId));
      dispatch(
        fetchGalleryImages({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          category: selectedCategory,
        }),
      );
    }
  };

  const handleToggleStatus = async (imageId, currentStatus) => {
    await dispatch(
      toggleImageStatus({ id: imageId, isActive: !currentStatus }),
    );
    dispatch(
      fetchGalleryImages({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        category: selectedCategory,
      }),
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(
      fetchGalleryImages({
        page: 1,
        limit: itemsPerPage,
        search: searchTerm,
        category: selectedCategory,
      }),
    );
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const totalPages = pagination?.totalPages || 1;

  // Get unique categories from images
  const categories = [
    ...new Set(images?.map((img) => img.category).filter(Boolean)),
  ];

  return (
    <>
      <Helmet>
        <title>Gallery - Admin</title>
      </Helmet>

      <div className="space-y-5">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Gallery</h1>
            <p className="text-gray-500 text-sm">Upload and manage gallery images</p>
          </div>
          <Link to="/admin/gallery/upload" className="btn btn-primary flex items-center gap-2 text-sm">
            <FaPlus size={12} />
            Upload Image
          </Link>
        </div>

        <div>
          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search images..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10 w-full"
                  />
                </div>
              </form>

              <div className="flex items-center gap-2">
                <FaFilter className="text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryFilter(e.target.value)}
                  className="form-select"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="bg-white rounded-lg shadow p-6">
            {loading ? (
              <div className="p-8 text-center">
                <div className="loading w-8 h-8 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading images...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <p className="text-red-600">{error}</p>
              </div>
            ) : images?.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {images.map((image) => (
                    <div
                      key={image._id}
                      className="bg-gray-50 rounded-lg overflow-hidden"
                    >
                      <div className="relative">
                        <img
                          src={image.imageUrl}
                          alt={image.title || "Gallery image"}
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() =>
                              handleToggleStatus(image._id, image.isActive)
                            }
                            className={`p-1 rounded ${
                              image.isActive
                                ? "bg-green-500 text-white"
                                : "bg-gray-500 text-white"
                            }`}
                            title={image.isActive ? "Active" : "Inactive"}
                          >
                            <FaEye className="text-sm" />
                          </button>
                        </div>
                      </div>

                      <div className="p-4">
                        <h3 className="font-medium text-sm mb-1 truncate">
                          {image.title || "Untitled"}
                        </h3>
                        {image.category && (
                          <p className="text-xs text-gray-600 mb-2">
                            {image.category}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mb-3">
                          {new Date(image.createdAt).toLocaleDateString()}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex gap-2">
                            <Link
                              to={`/gallery/${image._id}`}
                              className="text-blue-600 hover:text-blue-900 text-sm"
                              title="View"
                            >
                              <FaEye />
                            </Link>
                            <Link
                              to={`/admin/gallery/${image._id}/edit`}
                              className="text-green-600 hover:text-green-900 text-sm"
                              title="Edit"
                            >
                              <FaEdit />
                            </Link>
                            <button
                              onClick={() => handleDelete(image._id)}
                              className="text-red-600 hover:text-red-900 text-sm"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              image.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {image.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page{" "}
                        <span className="font-medium">{currentPage}</span> of{" "}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <FaChevronLeft />
                        </button>
                        <button
                          onClick={() =>
                            setCurrentPage(
                              Math.min(totalPages, currentPage + 1),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <FaChevronRight />
                        </button>
                      </nav>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <FaImage className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No images found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || selectedCategory
                    ? "Try adjusting your search or filter criteria."
                    : "Get started by uploading your first image."}
                </p>
                <Link to="/admin/gallery/upload" className="btn btn-primary">
                  <FaPlus className="mr-2" />
                  Upload Images
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminGallery;
